import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";
import { MonitorPlay, Settings, ShieldCheck, Smartphone, UtensilsCrossed } from "lucide-react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  api,
  dashboardSessionInvalidatedEvent,
  dashboardSessionStorageKey,
  loadSession,
  saveSession,
  type Session,
} from "./api.js";
import { Dashboard } from "./Dashboard.js";
import { ControllerPage } from "./ControllerPage.js";
import { managementUiEnabled, menuEnabled, playerEnabled } from "./featureFlags.js";
import { LoginPage } from "./LoginPage.js";
import { LandingPage } from "./LandingPage.js";
import { PlayerPage } from "./PlayerPage.js";
import { matchesConfiguredPlayerUrl } from "./playerRoute.js";
import { ResetPasswordPage } from "./ResetPasswordPage.js";
import { MenuPublicPage } from "./MenuPublicPage.js";
import type { PlatformBranding } from "./adminOperations.js";
import { currentBrowserIsMobilePhone, mobilePhoneRouteSurface } from "./mobileSupport.js";

const defaultBranding: PlatformBranding = {
  name: "Q-Assist TV",
  logoUrl: null,
  faviconUrl: null,
  playerUrl: null,
  primaryColor: "#087b82",
  secondaryColor: "#06656b",
  environmentMode: "development",
  clientDashboardEnabled: true,
};

function captureApkDeviceId(): string | null {
  const match = window.location.hash.match(/^#qassist-device-id=([0-9a-f-]{36})$/i);
  if (match?.[1]) {
    const value = match[1].toLowerCase();
    sessionStorage.setItem("qassist.apkDeviceId", value);
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
    return value;
  }
  return sessionStorage.getItem("qassist.apkDeviceId");
}

function DisabledSurface({
  feature,
  alternativePath,
  alternativeLabel,
}: {
  feature: "Management UI" | "Client dashboard" | "TV player";
  alternativePath?: string;
  alternativeLabel?: string;
}) {
  const isPlayer = feature === "TV player";
  const surfaceDescription =
    feature === "Client dashboard"
      ? "client dashboard"
      : isPlayer
        ? "TV player"
        : "management interface";
  const Icon = isPlayer ? MonitorPlay : Settings;
  return (
    <main className="feature-disabled">
      <section className="feature-disabled__card" aria-labelledby="disabled-surface-title">
        <span className="feature-disabled__icon">
          <Icon size={30} />
        </span>
        <span className="eyebrow">Surface unavailable</span>
        <h1 id="disabled-surface-title">{feature} is disabled</h1>
        <p>
          This deployment has turned off the {surfaceDescription}. Contact the platform
          administrator if this surface should be available.
        </p>
        {alternativePath && alternativeLabel ? (
          <Link className="button button--primary" to={alternativePath}>
            {alternativeLabel}
          </Link>
        ) : null}
      </section>
    </main>
  );
}

function ResolvingPublicRoute({ platformName }: { platformName: string }) {
  return (
    <main className="feature-disabled" aria-busy="true">
      <section className="feature-disabled__card" aria-live="polite">
        <span className="feature-disabled__icon">
          <MonitorPlay size={30} />
        </span>
        <span className="eyebrow">Opening display</span>
        <h1>Starting {platformName}</h1>
        <p>Checking whether this address is configured as the public TV player…</p>
      </section>
    </main>
  );
}

function ResolvingDashboardSession({
  platformName,
  failed,
  onRetry,
  onSignInAgain,
}: {
  platformName: string;
  failed: boolean;
  onRetry(): void;
  onSignInAgain(): void;
}) {
  return (
    <main className="feature-disabled" aria-busy={failed ? undefined : "true"}>
      <section className="feature-disabled__card" aria-live="polite">
        <span className="feature-disabled__icon">
          <ShieldCheck size={30} aria-hidden="true" />
        </span>
        <span className="eyebrow">{platformName}</span>
        <h1>{failed ? "Session check unavailable" : "Checking your session"}</h1>
        <p>
          {failed
            ? "The dashboard could not verify this saved sign-in. Try again or return to sign in."
            : "Confirming that this saved sign-in is still active…"}
        </p>
        {failed ? (
          <div className="button-row">
            <button className="button button--primary" type="button" onClick={onRetry}>
              Try again
            </button>
            <button className="button button--secondary" type="button" onClick={onSignInAgain}>
              Return to sign in
            </button>
          </div>
        ) : null}
      </section>
    </main>
  );
}

function MobilePhoneUnsupported({ branding }: { branding: PlatformBranding }) {
  const style = {
    "--brand-primary": branding.primaryColor,
    "--brand-secondary": branding.secondaryColor,
  } as CSSProperties;
  return (
    <main className="mobile-unsupported" style={style}>
      <section className="mobile-unsupported__card" aria-labelledby="mobile-unsupported-title">
        <span className="mobile-unsupported__icon">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt="" />
          ) : (
            <Smartphone size={34} aria-hidden="true" />
          )}
        </span>
        <span className="eyebrow">{branding.name}</span>
        <h1 id="mobile-unsupported-title">Mobile phones are not supported</h1>
        <p>
          This application is designed for desktop management screens and television displays. Open
          it on a desktop computer or TV.
        </p>
      </section>
    </main>
  );
}

export function App() {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [apkDeviceId] = useState<string | null>(() => captureApkDeviceId());
  const mobilePhone = currentBrowserIsMobilePhone();
  const brandingQuery = useQuery({
    queryKey: ["platform", "branding"],
    queryFn: () => api<PlatformBranding>("/api/v1/platform/branding"),
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  const sessionValidationQuery = useQuery({
    queryKey: ["auth", "session", session?.token],
    queryFn: () => api<Record<string, unknown>>("/api/v1/auth/me", {}, session?.token),
    enabled: Boolean(session),
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  const branding = brandingQuery.data ?? defaultBranding;

  useEffect(() => {
    const synchronizeDashboardSession = (event: StorageEvent) => {
      if (event.storageArea === localStorage && event.key === dashboardSessionStorageKey)
        setSession(loadSession());
    };
    const clearRejectedDashboardSession = () => setSession(null);
    window.addEventListener("storage", synchronizeDashboardSession);
    window.addEventListener(dashboardSessionInvalidatedEvent, clearRejectedDashboardSession);
    return () => {
      window.removeEventListener("storage", synchronizeDashboardSession);
      window.removeEventListener(dashboardSessionInvalidatedEvent, clearRejectedDashboardSession);
    };
  }, []);

  useEffect(() => {
    const documentUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    const landingTitle =
      location.pathname === "/" && !matchesConfiguredPlayerUrl(branding.playerUrl, documentUrl);
    document.title = landingTitle ? `${branding.name} | Managed digital signage` : branding.name;
    document.documentElement.style.setProperty("--brand-primary", branding.primaryColor);
    document.documentElement.style.setProperty("--brand-secondary", branding.secondaryColor);
    let themeColor = document.querySelector<HTMLMetaElement>("meta[name='theme-color']");
    if (!themeColor) {
      themeColor = document.createElement("meta");
      themeColor.name = "theme-color";
      document.head.append(themeColor);
    }
    themeColor.content = branding.primaryColor;
    let favicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (branding.faviconUrl) {
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.append(favicon);
      }
      favicon.href = branding.faviconUrl;
    } else {
      favicon?.remove();
    }
    return () => {
      document.documentElement.style.removeProperty("--brand-primary");
      document.documentElement.style.removeProperty("--brand-secondary");
    };
  }, [branding, location.hash, location.pathname, location.search]);

  function updateSession(next: Session | null): void {
    saveSession(next);
    setSession(next);
  }

  const appStyle = {
    "--brand-primary": branding.primaryColor,
    "--brand-secondary": branding.secondaryColor,
  } as CSSProperties;
  const managementHome = session
    ? session.user.mode === "admin"
      ? "/admin"
      : "/client"
    : "/login";
  const defaultPath = managementUiEnabled
    ? managementHome
    : playerEnabled
      ? "/player"
      : "/unavailable";
  const managementDisabled = (
    <DisabledSurface
      feature="Management UI"
      {...(playerEnabled ? { alternativePath: "/player", alternativeLabel: "Open TV player" } : {})}
    />
  );
  const clientDashboardDisabled = <DisabledSurface feature="Client dashboard" />;
  const playerDisabled = (
    <DisabledSurface
      feature="TV player"
      {...(managementUiEnabled
        ? { alternativePath: managementHome, alternativeLabel: "Open management UI" }
        : {})}
    />
  );
  const currentUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
  const androidTvWrapper = /QAssistAndroidTV/i.test(navigator.userAgent);
  const configuredPlayerRoute = matchesConfiguredPlayerUrl(branding.playerUrl, currentUrl);
  const controllerRoute =
    location.pathname === "/controller" || window.location.hostname === "tvcontrol.qassist.qa";
  const mobileRouteSurface = mobilePhoneRouteSurface({
    pathname: location.pathname,
    configuredPlayerRoute,
    brandingPending: brandingQuery.isPending,
  });
  const unmatchedRoute = brandingQuery.isPending ? (
    <ResolvingPublicRoute platformName={branding.name} />
  ) : configuredPlayerRoute ? (
    playerEnabled ? (
      <PlayerPage platformBranding={branding} deviceId={apkDeviceId} />
    ) : (
      playerDisabled
    )
  ) : (
    <Navigate to={defaultPath} replace />
  );
  const rootRoute = brandingQuery.isPending ? (
    <ResolvingPublicRoute platformName={branding.name} />
  ) : configuredPlayerRoute ? (
    playerEnabled ? (
      <PlayerPage platformBranding={branding} deviceId={apkDeviceId} />
    ) : (
      playerDisabled
    )
  ) : (
    <LandingPage branding={branding} />
  );

  if (controllerRoute)
    return (
      <div className="app-shell" style={appStyle}>
        <ControllerPage branding={branding} />
      </div>
    );

  if (
    mobilePhone &&
    mobileRouteSurface !== "player" &&
    mobileRouteSurface !== "menu" &&
    mobileRouteSurface !== "landing"
  ) {
    if (mobileRouteSurface === "resolving-public-route")
      return <ResolvingPublicRoute platformName={branding.name} />;
    return <MobilePhoneUnsupported branding={branding} />;
  }
  if (androidTvWrapper)
    return (
      <div className="app-shell" style={appStyle}>
        {playerEnabled ? (
          <PlayerPage platformBranding={branding} deviceId={apkDeviceId} />
        ) : (
          playerDisabled
        )}
      </div>
    );

  const dashboardSessionRoute = /^\/(?:login(?:-admin)?|admin|client)(?:\/|$)/.test(
    location.pathname,
  );
  if (session && dashboardSessionRoute && sessionValidationQuery.isPending)
    return (
      <div className="app-shell" style={appStyle}>
        <ResolvingDashboardSession
          platformName={branding.name}
          failed={false}
          onRetry={() => void sessionValidationQuery.refetch()}
          onSignInAgain={() => updateSession(null)}
        />
      </div>
    );
  if (session && dashboardSessionRoute && sessionValidationQuery.isError)
    return (
      <div className="app-shell" style={appStyle}>
        <ResolvingDashboardSession
          platformName={branding.name}
          failed
          onRetry={() => void sessionValidationQuery.refetch()}
          onSignInAgain={() => updateSession(null)}
        />
      </div>
    );

  return (
    <div className="app-shell" style={appStyle}>
      <Routes>
        <Route path="/" element={rootRoute} />
        <Route
          path="/menu/:restaurantSlug/*"
          element={
            menuEnabled ? (
              <MenuPublicPage />
            ) : (
              <main className="menu-public-state">
                <UtensilsCrossed size={34} />
                <h1>Menu is disabled</h1>
                <p>This deployment has turned off the public restaurant menu.</p>
              </main>
            )
          }
        />
        <Route
          path="/player"
          element={
            playerEnabled ? (
              <PlayerPage platformBranding={branding} deviceId={apkDeviceId} />
            ) : (
              playerDisabled
            )
          }
        />
        <Route
          path="/reset-password"
          element={
            managementUiEnabled ? <ResetPasswordPage branding={branding} /> : managementDisabled
          }
        />
        <Route
          path="/login"
          element={
            !managementUiEnabled ? (
              managementDisabled
            ) : session?.user.mode === "admin" ? (
              <Navigate to="/admin" replace />
            ) : !branding.clientDashboardEnabled ? (
              clientDashboardDisabled
            ) : session ? (
              <Navigate to="/client" replace />
            ) : (
              <LoginPage branding={branding} mode="client" onAuthenticated={updateSession} />
            )
          }
        />
        <Route
          path="/login-admin"
          element={
            !managementUiEnabled ? (
              managementDisabled
            ) : session ? (
              <Navigate to={session.user.mode === "admin" ? "/admin" : "/client"} replace />
            ) : (
              <LoginPage branding={branding} mode="admin" onAuthenticated={updateSession} />
            )
          }
        />
        <Route
          path="/admin/*"
          element={
            !managementUiEnabled ? (
              managementDisabled
            ) : session?.user.mode === "admin" ? (
              <Dashboard
                session={session}
                platformBranding={branding}
                onSessionChange={updateSession}
              />
            ) : (
              <Navigate to="/login-admin" replace />
            )
          }
        />
        <Route
          path="/client/*"
          element={
            !managementUiEnabled ? (
              managementDisabled
            ) : !branding.clientDashboardEnabled && !session?.impersonation ? (
              clientDashboardDisabled
            ) : session?.user.mode === "client" ? (
              <Dashboard
                session={session}
                platformBranding={branding}
                onSessionChange={updateSession}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/unavailable"
          element={
            managementUiEnabled ? (
              <Navigate to={managementHome} replace />
            ) : playerEnabled ? (
              <Navigate to="/player" replace />
            ) : (
              <DisabledSurface feature="Management UI" />
            )
          }
        />
        <Route path="*" element={unmatchedRoute} />
      </Routes>
    </div>
  );
}
